// pages/MyMentions.jsx
import React, { useEffect, useMemo, useState, useCallback, lazy, Suspense } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  FiAtSign, FiFileText, FiUser, FiArrowRight, FiSearch, FiX, FiInbox,
  FiPaperclip, FiDownload, FiExternalLink
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { fetchMyMentions } from '../api/notificationApi';
import '../styles/MyMentions.css';
import Header from '../components/header/header';
import Navvbar from '../components/header/Navvbar';
// ✅ NEW: emoji reactions on notes
import NoteReactions from './NoteReactions';

// ✅ Lazy-load the same STLViewer used in CaseTable
const STLViewer = lazy(() =>
  import('../components/dashboard/cases/newCase/case_id/caseTable/STLViewer')
);
// Set to true if you also want the "From: ... Created: ..." header line inside each note preview
const SHOW_NOTE_HEADER = false;

const MAX_TAGS = 3;
const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

// ✅ NEW: numeric id of the logged-in user (used to highlight "my" reaction)
const getMyUserId = () => {
  try {
    const info = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const raw = localStorage.getItem('userId') || info.userId || info._id;
    if (raw === undefined || raw === null || raw === '') return null;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  } catch (e) {
    return null;
  }
};

// ---------- helpers (unchanged) ----------
const decodeEntities = (s) => {
  try {
    const doc = new DOMParser().parseFromString(String(s), 'text/html');
    return doc.documentElement.textContent || '';
  } catch (e) {
    return String(s);
  }
};

const unescapeIfNeeded = (html) => {
  let s = String(html || '');
  for (let i = 0; i < 2; i += 1) {
    const hasRealTags = /<\/?[a-z][^>]*>/i.test(s);
    const looksEscaped = /&lt;\s*\/?\s*[a-z]/i.test(s);
    if (!hasRealTags && looksEscaped) {
      s = decodeEntities(s);
    } else {
      break;
    }
  }
  return s;
};

const buildNoteHtml = (raw) => {
  if (!raw) return '';
  const clean = DOMPurify.sanitize(unescapeIfNeeded(raw));
  if (SHOW_NOTE_HEADER) return clean;

  try {
    const doc = new DOMParser().parseFromString(
      `<div id="mm-root">${clean}</div>`,
      'text/html'
    );
    const root = doc.getElementById('mm-root');
    const first = root && root.firstElementChild;

    if (
      first &&
      first.tagName === 'DIV' &&
      /^\s*From:/i.test(first.textContent || '')
    ) {
      first.remove();
    }

    return root ? root.innerHTML : clean;
  } catch (e) {
    return clean;
  }
};

const htmlToText = (html) => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(
      `<div id="mm-root">${html
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')}</div>`,
      'text/html'
    );
    return (doc.getElementById('mm-root')?.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
  } catch (e) {
    return String(html).replace(/<(.|\n)*?>/g, ' ').replace(/\s+/g, ' ').trim();
  }
};

const getInitials = (name) => {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return 'Just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} day${d > 1 ? 's' : ''} ago`;
  return new Date(t).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
};

const isImage = (mimetype) => (mimetype || '').startsWith('image/');

const isPdf = (mimetype, name) =>
  (mimetype || '').toLowerCase() === 'application/pdf' ||
  /\.pdf$/i.test(name || '');

// ✅ NEW: Detect STL / PLY files
const isStlOrPly = (att) => {
  if (!att) return false;
  const name = (att.originalName || att.name || att.path || '').toLowerCase();
  const mime = (att.mimetype || '').toLowerCase();
  return (
    name.endsWith('.stl') ||
    name.endsWith('.ply') ||
    mime === 'application/sla' ||
    mime === 'model/stl' ||
    mime === 'application/octet-stream'
  );
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getAttachmentUrl = (att) => {
  if (!att) return '';
  if (att.url) {
    return att.url.startsWith('http') ? att.url : `${API_BASE_URL}${att.url}`;
  }
  if (att.path) {
    const filename = String(att.path).split(/[\\/]/).pop();
    return `${API_BASE_URL}/uploads/${filename}`;
  }
  return '';
};

const previewScrollStyle = {
  display: 'block',
  height: 'auto',
  maxHeight: '140px',
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitLineClamp: 'unset',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere'
};

// ---------- File preview modal ----------
const FilePreviewModal = ({ open, onClose, att }) => {
  // Close on ESC + lock scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !att) return null;

  const href = getAttachmentUrl(att);
  const displayName = att.originalName || att.name || 'File';
  const image = isImage(att.mimetype);
  const pdf = isPdf(att.mimetype, displayName);

  return (
    <div
      className="mm-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="mm-modal" role="dialog" aria-modal="true">
        <div className="mm-modal-header">
          <div className="mm-modal-title" title={displayName}>
            {image ? <FiFileText size={16} /> : <FiFileText size={16} />}
            <span>{displayName}</span>
            <span className="mm-modal-size">{formatFileSize(att.size)}</span>
          </div>
          <div className="mm-modal-actions">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="mm-modal-icon-btn"
              title="Open in new tab"
            >
              <FiExternalLink size={16} />
            </a>
            <a
              href={href}
              download={displayName}
              className="mm-modal-icon-btn"
              title="Download"
            >
              <FiDownload size={16} />
            </a>
            <button
              type="button"
              className="mm-modal-icon-btn mm-modal-close"
              onClick={onClose}
              title="Close"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>

        <div className="mm-modal-body">
          {image && (
            <img
              src={href}
              alt={displayName}
              className="mm-modal-image"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}

          {pdf && (
            <iframe
              src={href}
              title={displayName}
              className="mm-modal-iframe"
            />
          )}

          {!image && !pdf && (
            <div className="mm-modal-fallback">
              <FiFileText size={56} color="#0066cc" />
              <p className="mm-modal-fallback-title">{displayName}</p>
              <p className="mm-modal-fallback-hint">
                Preview is not available for this file type.
              </p>
              <a
                href={href}
                download={displayName}
                className="mm-modal-download-btn"
              >
                <FiDownload size={16} /> Download file
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ✅ NEW: STL/PLY full-screen viewer modal (same UI as CaseTable's STLFullViewModal)
const STLPreviewModal = ({ open, onClose, att }) => {
  const [isModelReady, setIsModelReady] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!open) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleEsc);
    };
  }, [open, onClose]);

  // Reset loaders when a new file is opened
  useEffect(() => {
    if (open) {
      setIsModelReady(false);
      setProgress(0);
    }
  }, [open, att]);

  const handleLoad = useCallback(() => setIsModelReady(true), []);
  const handleError = useCallback(() => setIsModelReady(true), []);

  if (!open || !att) return null;

  const fileUrl = getAttachmentUrl(att);
  const displayName = att.originalName || att.name || 'Model';
  const uploadedBy =
    att.uploadedBy || att.uploaded_by || att.uploaderName || att.uploader || 'Unknown User';

  const formatUploadTime = () => {
    const raw = att.createdAt || att.uploadedAt || att.date;
    if (!raw) return '—';
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '—';
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        zIndex: 9999,
        padding: '6px',
        boxSizing: 'border-box',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 'min(1800px, 96vw)',
          height: 'min(900px, 92vh)',
          backgroundColor: '#00a1d6',
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header bar */}
        <div
          style={{
            backgroundColor: '#004aad',
            backgroundImage: 'linear-gradient(115deg, #004aad 0%, #003b88 100%)',
            color: '#fff',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexShrink: 0,
            minHeight: '68px',
            boxSizing: 'border-box',
            borderBottom: '1px solid rgba(255,255,255,0.18)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                minWidth: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.13)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '10px',
              }}
            >
              <FiFileText size={19} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  lineHeight: 1.35,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={displayName}
              >
                {displayName}
              </div>
              <div
                style={{
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.72)',
                  letterSpacing: '0.7px',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
              >
                3D Model Viewer
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}
              title={`Uploaded by: ${uploadedBy}`}
            >
              <span
                style={{
                  fontSize: '10px',
                  color: 'rgba(255,255,255,0.68)',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                }}
              >
                Uploaded By
              </span>
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '180px',
                }}
              >
                {uploadedBy}
              </span>
            </div>

            <div
              style={{
                width: '1px',
                height: '32px',
                backgroundColor: 'rgba(255,255,255,0.25)',
              }}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <span
                style={{
                  fontSize: '10px',
                  color: 'rgba(255,255,255,0.68)',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                }}
              >
                Uploaded At
              </span>
              <span style={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {formatUploadTime()}
              </span>
            </div>

            <button
              onClick={onClose}
              aria-label="Close STL viewer"
              title="Close viewer"
              style={{
                backgroundColor: 'rgba(255,255,255,0.12)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                width: '34px',
                height: '34px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '17px',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.28)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)';
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Viewer area */}
        <div style={{ position: 'relative', flex: 1, minHeight: 0, width: '100%' }}>
          {!isModelReady && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#00a1d6',
                zIndex: 5,
              }}
            >
              <div
                style={{
                  width: '50px',
                  height: '50px',
                  border: '4px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#fff',
                  borderRadius: '50%',
                  animation: 'mm-stl-spin 0.8s linear infinite',
                }}
              />
              <p style={{ color: '#fff', marginTop: '15px', fontSize: '15px' }}>
                Loading 3D Model...
                {progress > 0 && progress < 100 ? ` ${progress}%` : ''}
              </p>
              <style>
                {`
                  @keyframes mm-stl-spin {
                    to { transform: rotate(360deg); }
                  }
                `}
              </style>
            </div>
          )}

          <div style={{ width: '100%', height: '100%' }}>
            <Suspense fallback={null}>
              <STLViewer
                fileUrl={fileUrl}
                size="extra-large"
                shouldLoad={true}
                onProgress={setProgress}
                onLoad={handleLoad}
                onError={handleError}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function MyMentions() {
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  // ✅ Preview modal state (image/pdf/other)
  const [previewAtt, setPreviewAtt] = useState(null);

  // ✅ STL/PLY viewer modal state
  const [stlPreviewAtt, setStlPreviewAtt] = useState(null);

  const openPreview = useCallback((att) => setPreviewAtt(att), []);
  const closePreview = useCallback(() => setPreviewAtt(null), []);

  const openStlPreview = useCallback((att) => setStlPreviewAtt(att), []);
  const closeStlPreview = useCallback(() => setStlPreviewAtt(null), []);

  // ✅ NEW: current user id + reaction updater
  const myUserId = useMemo(() => getMyUserId(), []);

  const handleReactionsUpdated = useCallback((noteId, reactions) => {
    setMentions((prev) =>
      prev.map((m) => (m.noteId === noteId ? { ...m, reactions } : m))
    );
  }, []);

  // ✅ Router: STL/PLY → 3D viewer, everything else → file preview modal
  const handleAttachmentClick = useCallback(
    (att) => {
      if (isStlOrPly(att)) openStlPreview(att);
      else openPreview(att);
    },
    [openPreview, openStlPreview]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchMyMentions();
        if (!cancelled) {
          if (data.success) setMentions(data.mentions || []);
          else setError(data.message || 'Failed to load mentions');
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || 'Failed to load mentions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const prepared = useMemo(
    () =>
      mentions.map((m) => {
        const html = buildNoteHtml(m.noteText);
        return { ...m, _html: html, _plain: htmlToText(html) };
      }),
    [mentions]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return prepared;
    return prepared.filter((m) =>
      [m.caseNumber, m.case_id, m.patientName, m.noteCreatedBy, m.subject, m._plain]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [prepared, query]);

  return (
    <>
      <Header />
      <Navvbar />
      <style>
        {`
          .mm-preview-html p { margin: 0 0 6px; }
          .mm-preview-html p:last-child { margin-bottom: 0; }
          .mm-preview-html img { max-width: 100%; height: auto; }
          .mm-preview-html ul, .mm-preview-html ol { margin: 0 0 6px; padding-left: 20px; }
          .mm-preview-html blockquote { margin: 0 0 6px; padding-left: 10px; border-left: 3px solid #dee2e6; }
          .mm-preview-html pre { white-space: pre-wrap; }
          .mm-preview-html table { max-width: 100%; }

          /* Attachments */
          .mm-attachments {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px dashed #e9ecef;
          }
          .mm-attachment-label {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 11px;
            color: #6c757d;
            font-weight: 600;
            width: 100%;
            margin-bottom: 2px;
          }
          .mm-attachment {
            display: flex;
            align-items: center;
            gap: 6px;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 4px 8px;
            font-size: 11px;
            max-width: 230px;
            text-decoration: none;
            color: #212529;
            transition: all 0.2s ease;
          }
          .mm-attachment:hover {
            background: #eef4fb;
            border-color: #8fc1f0;
            color: #0066cc;
            text-decoration: none;
          }
          .mm-attachment-icon {
            flex: 0 0 auto;
            color: #0066cc;
            display: flex;
            align-items: center;
          }
          .mm-attachment-thumb {
            width: 28px;
            height: 28px;
            object-fit: cover;
            border-radius: 4px;
            border: 1px solid #dee2e6;
            background: #fff;
            flex: 0 0 auto;
          }
          .mm-attachment-info {
            flex: 1;
            min-width: 0;
          }
          .mm-attachment-name {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-weight: 500;
          }
          .mm-attachment-size {
            color: #868e96;
            font-size: 10px;
          }
          .mm-attachment-dl {
            color: #0066cc;
            display: flex;
            align-items: center;
            flex: 0 0 auto;
            cursor: pointer;
            padding: 2px;
            border-radius: 3px;
          }
          .mm-attachment-dl:hover { background: #e1eefd; }

          /* ===== Preview modal ===== */
          .mm-modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.65);
            z-index: 2000;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
          }
          .mm-modal {
            background: #fff;
            border-radius: 10px;
            width: min(1000px, 96vw);
            max-height: 92vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
          }
          .mm-modal-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 10px 14px;
            border-bottom: 1px solid #e9ecef;
            background: #f8f9fa;
          }
          .mm-modal-title {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 13px;
            font-weight: 600;
            color: #212529;
            min-width: 0;
          }
          .mm-modal-title > span:first-of-type {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 55vw;
          }
          .mm-modal-size {
            color: #868e96;
            font-weight: 500;
            font-size: 11px;
            flex: 0 0 auto;
          }
          .mm-modal-actions {
            display: flex;
            align-items: center;
            gap: 4px;
            flex: 0 0 auto;
          }
          .mm-modal-icon-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 6px;
            border: 1px solid transparent;
            background: transparent;
            color: #495057;
            cursor: pointer;
            text-decoration: none;
            transition: all 0.15s ease;
          }
          .mm-modal-icon-btn:hover {
            background: #e9ecef;
            color: #0066cc;
            border-color: #dee2e6;
            text-decoration: none;
          }
          .mm-modal-close:hover {
            background: #f8d7da;
            color: #dc3545;
          }
          .mm-modal-body {
            flex: 1;
            min-height: 0;
            overflow: auto;
            background: #f1f3f5;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 12px;
          }
          .mm-modal-image {
            max-width: 100%;
            max-height: calc(92vh - 100px);
            object-fit: contain;
            border-radius: 6px;
            background: #fff;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
          }
          .mm-modal-iframe {
            width: 100%;
            height: calc(92vh - 100px);
            border: none;
            border-radius: 6px;
            background: #fff;
          }
          .mm-modal-fallback {
            text-align: center;
            padding: 40px 20px;
            color: #495057;
          }
          .mm-modal-fallback-title {
            margin: 14px 0 4px;
            font-weight: 600;
            font-size: 14px;
            word-break: break-word;
          }
          .mm-modal-fallback-hint {
            color: #868e96;
            font-size: 12px;
            margin-bottom: 16px;
          }
          .mm-modal-download-btn {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: #0066cc;
            color: #fff;
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 13px;
            font-weight: 600;
            text-decoration: none;
            transition: background 0.15s ease;
          }
          .mm-modal-download-btn:hover {
            background: #0052a3;
            color: #fff;
            text-decoration: none;
          }
          @media (max-width: 600px) {
            .mm-modal-title > span:first-of-type { max-width: 40vw; }
          }
        `}
      </style>
      <div className="mm-page">
        <div className="mm-header">
          <div className="mm-heading">
            <span className="mm-heading-icon"><FiAtSign /></span>
            <div>
              <h4 className="mm-title">
                My Mentions
                {!loading && !error && mentions.length > 0 && (
                  <span className="mm-count">{mentions.length}</span>
                )}
              </h4>
              <p className="mm-subtitle">Case notes where you have been tagged</p>
            </div>
          </div>

          {!loading && !error && mentions.length > 0 && (
            <div className="mm-search">
              <FiSearch className="mm-search-icon" size={16} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search case, patient, author…"
                aria-label="Search mentions"
              />
              {query && (
                <button
                  type="button"
                  className="mm-search-clear"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : mentions.length === 0 ? (
          <div className="mm-state">
            <span className="mm-state-icon"><FiInbox /></span>
            <strong>No mentions yet</strong>
            <span>You have not been mentioned in any notes yet.</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mm-state">
            <span className="mm-state-icon"><FiSearch /></span>
            <strong>No results</strong>
            <span>Nothing matches "{query}".</span>
          </div>
        ) : (
          <div className="mm-grid">
            {filtered.map((m) => {
              const caseUrl = `/case/${m.case_id}?noteId=${m.noteId}`;
              const tags = Array.isArray(m.mentions) ? m.mentions : [];
              const author = m.noteCreatedBy || 'Unknown';
              const fullDate = m.noteCreatedAt ? new Date(m.noteCreatedAt).toLocaleString() : '';

              return (
                <article key={`${m.caseId}-${m.noteId}`} className="mm-card">
                  <div className="mm-card-top">
                    <Link to={caseUrl} className="mm-case" title="Open case">
                      <FiFileText size={14} />
                      Case {m.caseNumber || m.case_id}
                    </Link>
                    {m.subject && <span className="mm-subject" title={m.subject}>{m.subject}</span>}
                  </div>

                  <div className="mm-patient">
                    <FiUser size={15} />
                    <span>{m.patientName || 'Patient'}</span>
                  </div>

                  {m._plain && (
                    <div
                      className="mm-preview mm-preview-html"
                      style={previewScrollStyle}
                      dangerouslySetInnerHTML={{ __html: m._html }}
                    />
                  )}

                  {/* Attachments — STL/PLY open 3D viewer, others open file preview */}
                  {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                    <div className="mm-attachments">
                      <div className="mm-attachment-label">
                        <FiPaperclip size={11} /> Attachments ({m.attachments.length})
                      </div>
                      {m.attachments.map((att) => {
                        const href = getAttachmentUrl(att);
                        const displayName = att.originalName || att.name || 'File';
                        const img = isImage(att.mimetype);
                        const is3D = isStlOrPly(att);
                        return (
                          <a
                            key={att._id || att.name}
                            href={href}
                            onClick={(e) => {
                              // open in modal instead of navigating
                              e.preventDefault();
                              handleAttachmentClick(att);
                            }}
                            className="mm-attachment"
                            title={is3D ? `View 3D model: ${displayName}` : `Preview ${displayName}`}
                          >
                            {img ? (
                              <img
                                src={href}
                                alt=""
                                className="mm-attachment-thumb"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <span className="mm-attachment-icon">
                                <FiFileText size={16} />
                              </span>
                            )}
                            <span className="mm-attachment-info">
                              <div className="mm-attachment-name">{displayName}</div>
                              <div className="mm-attachment-size">{formatFileSize(att.size)}</div>
                            </span>
                            <span
                              className="mm-attachment-dl"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const a = document.createElement('a');
                                a.href = href;
                                a.download = displayName;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                              }}
                              title="Download"
                            >
                              <FiDownload size={13} />
                            </span>
                          </a>
                        );
                      })}
                    </div>
                  )}

                  {/* ✅ NEW: react to this note (you are always a mentioned user here) */}
                  <NoteReactions
                    caseId={m.case_id}
                    noteId={m.noteId}
                    reactions={m.reactions}
                    canReact
                    myUserId={myUserId}
                    onUpdated={handleReactionsUpdated}
                  />

                  {tags.length > 0 && (
                    <div className="mm-tags">
                      {tags.slice(0, MAX_TAGS).map((x) => (
                        <span className="mm-tag" key={x.userId} title={`@${x.userName}`}>
                          @{x.userName}
                        </span>
                      ))}
                      {tags.length > MAX_TAGS && (
                        <span className="mm-tag more">+{tags.length - MAX_TAGS}</span>
                      )}
                    </div>
                  )}

                  <div className="mm-foot">
                    <div className="mm-foot-inner">
                      <div className="mm-author">
                        <span className="mm-avatar">{getInitials(author)}</span>
                        <div className="mm-author-text">
                          <div className="mm-author-name" title={author}>{author}</div>
                          <div className="mm-time" title={fullDate}>{timeAgo(m.noteCreatedAt)}</div>
                        </div>
                      </div>
                      <Link to={caseUrl} className="mm-open">
                        Open <FiArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* ✅ File preview modal (images, PDFs, other files) */}
      <FilePreviewModal
        open={!!previewAtt}
        att={previewAtt}
        onClose={closePreview}
      />

      {/* ✅ STL/PLY 3D viewer modal (same STLViewer as CaseTable) */}
      <STLPreviewModal
        open={!!stlPreviewAtt}
        att={stlPreviewAtt}
        onClose={closeStlPreview}
      />
    </>
  );
}